'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import DocumentConfidenceLabel from './DocumentConfidenceLabel';
import DocumentConfidenceMeter from './DocumentConfidenceMeter';
import { TDocumentConfidenceLevel } from './types';

export type TConfidenceAccordionProps = {
  /** The section's name, for example `KYC/KYB`. */
  title: string;
  /**
   * The document confidence outcome for this section, shown on the summary row.
   *
   * Omit when there is no level to show, which covers both a section whose outcome could not be
   * determined and one that carries no outcome at all. The summary then renders the title and the
   * chevron and nothing else, rather than a meter at zero: an empty ladder is a reading of the
   * customer's documents, and "we have no reading" is not the same statement as "the reading is
   * nothing".
   */
  level?: TDocumentConfidenceLevel;
  /**
   * Holds the outcome's place with a skeleton while it is being fetched.
   *
   * Distinct from omitting `level`, and for the same reason the check cards keep the two apart: a
   * summary row that silently shows no outcome while a request is in flight is indistinguishable
   * from one whose outcome genuinely could not be determined.
   */
  isLoading?: boolean;
  /** Open on first render. The KYC section is the page's primary content, so it opens. */
  defaultExpanded?: boolean;
  children: ReactNode;
  'data-testid'?: string;
};

/**
 * A collapsible verification section whose summary row carries its confidence outcome.
 *
 * The outcome sits on the summary row rather than inside the body deliberately: when the section is
 * collapsed that row is the only thing carrying it, and an earlier draft that restated the level
 * above the table was asked in review to drop the restatement, not the summary.
 *
 * **Document confidence only, for now.** The level is typed to the document axis rather than to a
 * union of both, because the one section that has an outcome is KYC/KYB and its outcome is the
 * document ladder. The alternative, an `axis` prop, would let a caller pair `Substantial` with the
 * biometric ladder, which has no such level and only two steps. If a biometric section ever appears
 * this takes a discriminated union the way the check cards do.
 *
 * **The meter is decorative here**, with no `aria-label`, because `DocumentConfidenceLabel` states
 * the same level in words directly beside it. Labelling it would make a screen reader say the
 * outcome twice.
 */
export default function ConfidenceAccordion({
  title,
  level,
  isLoading = false,
  defaultExpanded = false,
  children,
  'data-testid': dataTestId,
}: Readonly<TConfidenceAccordionProps>): ReactElement {
  // Read through the hook rather than an `sx` callback, because `maxWidth` is a plain prop on the
  // meter rather than a style slot, and a literal here would be exactly the hardcoded dimension
  // ADR-0044/0078 rules out.
  const theme = useTheme();

  return (
    <Accordion defaultExpanded={defaultExpanded} data-testid={dataTestId}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-busy={isLoading}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={(t: Theme) => ({ width: '100%', gap: t.semantic.spacing.base })}
        >
          <Typography>{title}</Typography>

          {isLoading ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={(t: Theme) => ({ gap: t.semantic.spacing.sm })}
            >
              {/* Sized from the meter's own footprint rather than a literal, so the placeholder
                  cannot drift away from the thing it stands in for. */}
              <Skeleton
                variant="rounded"
                sx={(t: Theme) => ({
                  width: t.semantic.size['3xs'],
                  height: t.semantic.spacing['2xs'],
                })}
              />
              <Skeleton variant="text" sx={(t: Theme) => ({ width: t.semantic.size['4xs'] })} />
            </Stack>
          ) : (
            level && (
              <Stack
                direction="row"
                alignItems="center"
                sx={(t: Theme) => ({ gap: t.semantic.spacing.sm })}
              >
                {/* A definite width, not the meter's `maxWidth`. `SegmentedMeter` sizes itself
                    `width: 100%` under a `maxWidth`, and a percentage needs a container whose width
                    is already known. This row shrinks to fit its own contents, so the percentage
                    resolves against a width that depends on it and the bars collapse to nothing:
                    the summary row rendered the level name with no ladder beside it. Giving the
                    wrapper the width the meter would have taken makes the percentage resolvable. */}
                <Box sx={{ flex: 'none', width: theme.semantic.size['3xs'] }}>
                  <DocumentConfidenceMeter level={level} />
                </Box>
                <DocumentConfidenceLabel level={level} />
              </Stack>
            )
          )}
        </Stack>
      </AccordionSummary>

      {/* No level restatement above the body. It is on the row directly above, and repeating it is
          what review asked to drop. */}
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}
